import React, { useState, useEffect } from "react";
import * as Icon from "react-feather";
import {
  Button,
  Form,
  Input,
  Switch,
  Modal,
  Tabs,
  DatePicker,
  TimePicker,
  Space,
  Select,
  Row,
  Col,
} from "antd";
import { useDispatch } from "react-redux";
import activityService from "../../services/activity-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import { UploadOutlined } from "@ant-design/icons";
import UploadImage from "../../components/upload/image";
import UploadFile from "../../components/upload/file";
import EditableTagGroup from "../../components/input/editable-tag-group";
import {
  fileTYPE_IMG,
  groupType,
  PREVIEW_TYPE,
} from "../../constants/app-constants";
import fileUtils from "../../utils/file-utils";
import HtmlEditor from "../../components/input/html-editor";
import { FORM_LANG, formText, imageSizeHint, langOfTab } from '../../constants/form-i18n';
import moment from "moment";
import { VISIBILITY_DEFAULT, ACTIVITY_TYPE_OPTIONS } from "../../constants/app-constants";
import VisibilityFormItem from "../../components/input/visibility-form-item";
import { openWebsitePreview } from "../../utils/preview-utils";

const { TabPane } = Tabs;

/** NMU-463: ActivityBean stores startTime/endTime as 24h "HH:mm" strings. */
const TIME_FORMAT = "HH:mm";

const defaultValue = {
  serviceActivityImage: null,
  serviceActivityFile: [],
  serviceActivityDate: null,
  isOpenNewTab: false,
  keywordList: [],
  urlSuffix: null,
};

const AddEditActivityDialog = ({
  isShow,
  onCancel,
  onSaveComplete,
  editData,
  renderGroupMenuOption,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [serviceActivityImage, setServiceActivityImage] = useState(
    defaultValue.serviceActivityImage
  );
  const [serviceActivityFile, setServiceActivityFile] = useState(
    defaultValue.serviceActivityFile
  );
  const [serviceActivityDate, setServiceActivityDate] = useState(
    defaultValue.serviceActivityDate
  );
  // NMU-463: operating hours, kept as "HH:mm" strings on the bean.
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [allDay, setAllDay] = useState(false);
  const [keywordList, setKeywordList] = useState(defaultValue.keywordList);
  const [editorContentTH, setEditorContentTH] = useState("");
  const [editorContentEN, setEditorContentEN] = useState("");
  // NMU-444: the active tab drives the language of the whole dialog.
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) {
      setServiceActivityImage(defaultValue.serviceActivityImage);
      setKeywordList(defaultValue.keywordList);
    }
  }, [isShow]);

  useEffect(() => {
    if (isShow && editData) {
      form.setFieldsValue({
        titleTH: editData.title.titleTH,
        titleEN: editData.title.titleEN,
        status: editData.status,
        totalView: editData.totalView,
        facebookLink: editData.facebookLink,
        groupMenuList: editData.groupMenuList ? editData.groupMenuList : [],
        urlSuffix: editData.urlSuffix,
        venueTH: editData.venueTH,
        venueEN: editData.venueEN,
        visibility: editData.visibility ? editData.visibility : VISIBILITY_DEFAULT,
        activityType: editData.activityType ? editData.activityType : undefined,
      });
      setServiceActivityImage(editData.attachImage);
      setServiceActivityFile(editData.documentList);
      setServiceActivityDate(moment(editData.date));
      setStartTime(editData.startTime ? moment(editData.startTime, TIME_FORMAT) : null);
      setEndTime(editData.endTime ? moment(editData.endTime, TIME_FORMAT) : null);
      setAllDay(!!editData.allDay);
      setKeywordList(
        editData.keywordList ? editData.keywordList : defaultValue.keywordList
      );
      setEditorContentTH(editData.content.contentTH);
      setEditorContentEN(editData.content.contentEN);
    } else {
      form.setFieldsValue({
        titleTH: null,
        titleEN: null,
        facebookLink: null,
        groupMenuList: [],
        urlSuffix: null,
        visibility: VISIBILITY_DEFAULT,
        activityType: undefined,
      });
      setServiceActivityFile([]);
      setServiceActivityDate(null);
      setStartTime(null);
      setEndTime(null);
      setAllDay(false);
      setEditorContentTH("");
      setEditorContentEN("");
    }
  }, [isShow, editData]);

  const props = {
    action: "//jsonplaceholder.typicode.com/posts/",
    listType: "picture",
    multiple: false,
  };

  const onChangeValueTH = (value) => {
    setEditorContentTH(value);
  };

  const onChangeValueEN = (value) => {
    setEditorContentEN(value);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      try {
        dispatch(appLoaderAction.enable());

        if (!editData) {
          let bean = {
            bean: {
              title: {
                titleTH: values.titleTH,
                titleEN: values.titleEN,
              },
              keywordList: keywordList,
              venueTH: values.venueTH,
              venueEN: values.venueEN,
              content: {
                contentTH: editorContentTH,
                contentEN: editorContentEN,
              },
              attachImage: serviceActivityImage,
              documentList: serviceActivityFile,
              date: serviceActivityDate,
              // NMU-463
              startTime: allDay ? null : formatTime(startTime),
              endTime: allDay ? null : formatTime(endTime),
              allDay: allDay,
              totalView: 0,
              status: values.status,
              facebookLink: values.facebookLink,
              groupMenuList: values.groupMenuList,
              urlSuffix: values.urlSuffix,
              visibility: values.visibility,
              activityType: values.activityType,
            },
          };

          await activityService.add(bean);
        } else {
          // let data = [];
          let bean = JSON.parse(JSON.stringify(editData));
          bean.title.titleTH = values.titleTH;
          bean.title.titleEN = values.titleEN;
          bean.content.contentTH = editorContentTH;
          bean.content.contentEN = editorContentEN;
          bean.keywordList = keywordList;
          bean.venueTH = values.venueTH;
          bean.venueEN = values.venueEN;
          bean.attachImage = serviceActivityImage;
          bean.status = values.status;
          bean.totalView = editData.totalView;
          bean.date = serviceActivityDate;
          // NMU-463
          bean.startTime = allDay ? null : formatTime(startTime);
          bean.endTime = allDay ? null : formatTime(endTime);
          bean.allDay = allDay;
          bean.documentList = serviceActivityFile;
          bean.facebookLink = values.facebookLink;
          bean.groupMenuList = values.groupMenuList;
          bean.urlSuffix = values.urlSuffix;
          bean.visibility = values.visibility;
          bean.activityType = values.activityType;
          const editResult = {
            bean: {
              ...bean,
            },
          };

          await activityService.edit(editResult);
        }

        dispatch(appLoaderAction.disale());

        onSaveComplete();

        notifyUtitls.success(t('successTitle'), t('saveSuccess'));
      } catch (err) {
        notifyUtitls.error(err);
        dispatch(appLoaderAction.disale());
      }
    } catch (errorInfo) {
      notifyUtitls.warning(t('warningTitle'), t('incompleteForm'));
    }
  };

  const onTimeChange = (value, dateString) => {
    setServiceActivityDate(moment(value));
  };

  /** NMU-463: moment → the "HH:mm" the bean stores, or null when unset. */
  const formatTime = (value) => (value ? moment(value).format(TIME_FORMAT) : null);

  const onToggleAllDay = (checked) => {
    setAllDay(checked);
    if (checked) {
      setStartTime(null);
      setEndTime(null);
    }
  };

  const onUploadTemp = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setServiceActivityImage(bean);
    }
  };

  const onUploadFile = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setServiceActivityFile([...serviceActivityFile, bean]);
    }
  };

  const onPreview = async () => {
    try {
      dispatch(appLoaderAction.enable());

      const values = await form.validateFields();
      let previewBean = JSON.parse(JSON.stringify(editData));

      previewBean.title = {
        titleTH: values.titleTH,
        titleEN: values.titleEN,
      };
      previewBean.content = {
        contentTH: editorContentTH,
        contentEN: editorContentEN,
      };

      previewBean.attachImage = serviceActivityImage;
      previewBean.date = serviceActivityDate;
      previewBean.documentList = serviceActivityFile;
      previewBean.facebookLink = values.facebookLink;
      previewBean.groupMenuList = values.groupMenuList;
      previewBean.urlSuffix = values.urlSuffix;

      await openWebsitePreview({
        type: PREVIEW_TYPE.activity,
        bean: previewBean,
        path: (token) =>
          `/activity/${previewBean.urlSuffix || previewBean.activityId || token}/`,
      });
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  return (
    <div className="container h-100">
      <div className="title-page">
        <Modal
          title={t(editData ? 'editActivity' : 'addActivity')}
          centered
          width={750}
          visible={isShow}
          onCancel={onCancel}
          destroyOnClose
          footer={[
            <Button key="submit" type="primary" onClick={onSave}>
              <Icon.Save className="me-2" size={17} />
              {t('save')}
            </Button>,
            <Button key="back" className="btn-gray" onClick={onCancel}>
              {t('cancel')}
            </Button>,
            <Button className="mx-2 btn-line" onClick={() => onPreview()}>
              <Icon.Eye className="me-1" size={18} /> {t('previewWebsite')}
            </Button>,
          ]}
        >
          <Form
            layout="vertical"
            name="add_edit_user"
            preserve={false}
            form={form}
            initialValues={{
              status: true,
              visibility: VISIBILITY_DEFAULT,
            }}
            validateMessages={{
              required: t('required'),
            }}
          >
            <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
              <TabPane tab={t('tabThai')} key="1" forceRender>
                <Form.Item
                  label={t('activityTitle')}
                  name="titleTH"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t('activityContent')}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <div>
                    <HtmlEditor
                      lang={lang}
                      key="th"
                      id="th"
                      showGalleryButtons={false}
                      setContents={editorContentTH}
                      onChange={onChangeValueTH}
                    />
                  </div>
                </Form.Item>
                {/* NMU-427: the venue is language-specific, so it lives in the
                    language tabs like every other translated field. */}
                <Form.Item label={t('activityVenue')} name="venueTH">
                  <Input />
                </Form.Item>
              </TabPane>
              <TabPane tab={t('tabEnglish')} key="2" forceRender>
                <Form.Item label="Title" name="titleEN" className="mb-4">
                  <Input />
                </Form.Item>
                <Form.Item label="Content" className="mb-4">
                  <HtmlEditor
                      lang={lang}
                    key="en"
                    id="en"
                    showGalleryButtons={false}
                    setContents={editorContentEN}
                    onChange={onChangeValueEN}
                  />
                </Form.Item>
                <Form.Item label="Event Location" name="venueEN" className="mb-4">
                  <Input />
                </Form.Item>
              </TabPane>
            </Tabs>

            <Form.Item
              label={t('activityDate')}
              name="serviceActivityDate"
              rules={[
                {
                  required: serviceActivityDate ? false : true,
                },
              ]}
            >
              <Space direction="vertical" size={12}>
                <DatePicker
                  showTime
                  onChange={onTimeChange}
                  defaultValue={serviceActivityDate}
                  format="DD MMMM YYYY hh:mm:ss"
                />
              </Space>
            </Form.Item>

            {/*
              NMU-463: the calendar's clock badge and its "เวลาปฏิบัติการ" row had
              no source field — the payload carried no time at all.
            */}
            <Form.Item label={t('activityTime')}>
              <Row gutter={16} align="middle">
                <Col>
                  <TimePicker
                    id="activity_start_time"
                    format={TIME_FORMAT}
                    minuteStep={5}
                    disabled={allDay}
                    value={startTime}
                    onChange={setStartTime}
                    placeholder={t('activityStartTime')}
                  />
                </Col>
                <Col>–</Col>
                <Col>
                  <TimePicker
                    id="activity_end_time"
                    format={TIME_FORMAT}
                    minuteStep={5}
                    disabled={allDay}
                    value={endTime}
                    onChange={setEndTime}
                    placeholder={t('activityEndTime')}
                  />
                </Col>
                <Col>
                  <Switch
                    id="activity_all_day"
                    checked={allDay}
                    onChange={onToggleAllDay}
                    className="me-2"
                  />
                  <span>{t('activityAllDay')}</span>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item label={t('attachment')} name="fileHeader">
              <UploadFile
                {...props}
                onChange={onUploadFile}
                fileList={serviceActivityFile}
              >
                <Button type="dashed" icon={<UploadOutlined />}>
                  Upload File
                </Button>
              </UploadFile>
              {/* {serviceActivityFile ? (
                                serviceActivityFile.map((file) => {
                                    return (
                                        <div>
                                            <a href={
                                                fileUtils.getFileRenderUrl(
                                                    groupType.ACTIVITY,
                                                    editData ? editData.activityId : null,
                                                    file.originalFilename
                                                )} download>
                                                {file.originalFilename}
                                            </a>
                                        </div>
                                    )
                                })
                            ) : 'อัปโหลดไฟล์'} */}
            </Form.Item>

            <Form.Item
              label={t('image')}
              valuePropName="photoList"
              name="imageHeader"
              rules={[
                {
                  required: serviceActivityImage ? false : true,
                  // NMU-532: a picture is uploaded, not typed.
                  message: 'กรุณาอัปโหลดรูปภาพ',
                },
              ]}
            >
              <UploadImage
                listType="picture-card"
                accept={fileTYPE_IMG}
                showUploadList={false}
                onChange={onUploadTemp}
                className={serviceActivityImage ? "view-upload" : null}
                // limitWidth={1900}
                // limitHeight={710}
              >
                {serviceActivityImage ? (
                  <img
                    src={
                      serviceActivityImage.tempFilename
                        ? fileUtils.getTempFileRenderUrl(
                            serviceActivityImage.tempFilename,
                            serviceActivityImage.type
                          )
                        : fileUtils.getFileRenderUrl(
                            groupType.ACTIVITY,
                            editData ? editData.activityId : null,
                            serviceActivityImage.originalFilename
                          )
                    }
                    alt="avatar"
                    style={{ width: "100%" }}
                  />
                ) : (
                  t('uploadFile')
                )}
              </UploadImage>
              <label className="text-danger">{imageSizeHint(lang, '1900x710 pixels')}</label>
            </Form.Item>

            <Form.Item label={t('keywords')}>
              <EditableTagGroup onChange={setKeywordList} tags={keywordList} lang={lang} />
            </Form.Item>

            <Form.Item label="FacebookLink" name="facebookLink">
              <Input />
            </Form.Item>

            <Form.Item
              label="URL Name"
              name="urlSuffix"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item label={t('status')} name="status" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item
              label={t('userGroup')}
              name="groupMenuList"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select customWidth="100%" mode="multiple" showArrow>
                {renderGroupMenuOption()}
              </Select>
            </Form.Item>

            <Form.Item label={t('activityType')} name="activityType">
              <Select customWidth="100%" allowClear placeholder={t('selectActivityType')}>
                {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <VisibilityFormItem lang={lang} />
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AddEditActivityDialog;
